import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { Box, Button, Tooltip } from 'hephaestus/web';
import type { ChangeEvent, SyntheticEvent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FLAG_CATEGORY_FILTER_OPTIONS,
  getReconFlagStateLabel,
  getReconFlagTypeLabel,
  RECON_FLAG_STATE_FILTER_VALUES,
  RECON_FLAGS_AGE_FILTER_DAYS,
  RECON_FLAGS_AGE_FILTER_I18N,
  RECON_FLAGS_MAX_ACCOUNT_IDS,
  severityFilterAccentColor,
  sortReconFlagCategoryFilterOptionsByLabel,
  sortReconFlagStateFilterValuesByLabel,
  sortReconFlagTypesByLabel,
} from '../utils/reconFlags.config';
import {
  formatReconFlagsClientOptionLabel,
  type ReconFlagsClientOption,
} from '../utils/reconFlagsClientSearch';
import {
  getReconFlagsFiltersDefaultSelectSx,
  getReconFlagsFiltersSearchFieldSx,
  getReconFlagsFiltersSearchWrapperSx,
  getReconFlagsFiltersStateSelectSx,
  reconFlagsFiltersControlsGridSx,
  reconFlagsFiltersSeverityPillsRowSx,
  reconFlagsFiltersToolbarSx,
} from './reconFlagsFilters.styles';
import type { ReconFlagsFiltersCardProps } from './reconFlagsFiltersCard.types';
import { SeverityFilterPill } from './severityFilterPill';

export const ReconFlagsFiltersCard = ({
  theme,
  severityTab,
  onSeverityTabChange,
  flagState,
  onFlagStateChange,
  flagType,
  onFlagTypeChange,
  flagTypes,
  categoryFilter,
  onCategoryFilterChange,
  agingFilter,
  onAgingFilterChange,
  selectedClient,
  onSelectedClientChange,
  onSearchClients,
  onSearchClientsError,
  clientSearchDebounceMs,
  clientSearchMinChars,
  hasActiveFilters,
  onClearFilters,
}: ReconFlagsFiltersCardProps) => {
  const { t } = useTranslation(['reconciliation']);
  const sortedFlagTypes = useMemo(
    () => sortReconFlagTypesByLabel(t, flagTypes),
    [flagTypes, t],
  );
  const sortedCategoryFilterOptions = useMemo(
    () =>
      sortReconFlagCategoryFilterOptionsByLabel(
        t,
        FLAG_CATEGORY_FILTER_OPTIONS,
      ),
    [t],
  );
  const sortedStateFilterValues = useMemo(
    () =>
      sortReconFlagStateFilterValuesByLabel(t, RECON_FLAG_STATE_FILTER_VALUES),
    [t],
  );

  return (
    <>
      <Box sx={reconFlagsFiltersToolbarSx as any}>
        <Box sx={reconFlagsFiltersSeverityPillsRowSx as any}>
          <SeverityFilterPill
            label={t('reconFlags.severity.all')}
            active={severityTab === 'ALL'}
            inactiveBorderAndText={String(theme.color.outline)}
            onClick={() => onSeverityTabChange('ALL')}
            theme={theme}
          />
          <SeverityFilterPill
            label={t('reconFlags.severity.critical')}
            active={severityTab === 'CRITICAL'}
            inactiveBorderAndText={severityFilterAccentColor(theme, 'CRITICAL')}
            onClick={() => onSeverityTabChange('CRITICAL')}
            theme={theme}
          />
          <SeverityFilterPill
            label={t('reconFlags.severity.high')}
            active={severityTab === 'HIGH'}
            inactiveBorderAndText={severityFilterAccentColor(theme, 'HIGH')}
            onClick={() => onSeverityTabChange('HIGH')}
            theme={theme}
          />
          <SeverityFilterPill
            label={t('reconFlags.severity.medium')}
            active={severityTab === 'MEDIUM'}
            inactiveBorderAndText={severityFilterAccentColor(theme, 'MEDIUM')}
            onClick={() => onSeverityTabChange('MEDIUM')}
            theme={theme}
          />
          <SeverityFilterPill
            label={t('reconFlags.severity.low')}
            active={severityTab === 'LOW'}
            inactiveBorderAndText={severityFilterAccentColor(theme, 'LOW')}
            onClick={() => onSeverityTabChange('LOW')}
            theme={theme}
          />
        </Box>
        {hasActiveFilters && (
          <Button
            variant='text'
            color='primary'
            size='small'
            onClick={onClearFilters}
          >
            {t('reconFlags.filters.clearFilters')}
          </Button>
        )}
      </Box>
      <Box sx={reconFlagsFiltersControlsGridSx as any}>
        <TextField
          select
          size='small'
          label={t('state')}
          value={flagState}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onFlagStateChange(e.target.value)
          }
          fullWidth
          sx={getReconFlagsFiltersStateSelectSx(theme) as any}
        >
          {sortedStateFilterValues.map((state) => (
            <MenuItem key={state} value={state}>
              {getReconFlagStateLabel(t, state)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size='small'
          label={t('reconFlags.filters.flagType')}
          value={flagType ?? 'any'}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const v = e.target.value;
            onFlagTypeChange(v !== 'any' ? v : undefined);
          }}
          fullWidth
          sx={getReconFlagsFiltersDefaultSelectSx(theme) as any}
        >
          <MenuItem value='any'>{t('reconFlags.filters.any')}</MenuItem>
          {sortedFlagTypes.map((ft: string) => (
            <MenuItem key={ft} value={ft}>
              {getReconFlagTypeLabel(t, ft)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size='small'
          label={t('reconFlags.filters.category')}
          value={categoryFilter}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onCategoryFilterChange(e.target.value)
          }
          fullWidth
          sx={getReconFlagsFiltersDefaultSelectSx(theme) as any}
        >
          <MenuItem value='any'>{t('reconFlags.filters.any')}</MenuItem>
          {sortedCategoryFilterOptions.map(({ value, i18nKey }) => (
            <MenuItem key={value} value={value}>
              {t(`reconFlags.categories.${i18nKey}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size='small'
          label={t('reconFlags.filters.aging')}
          value={agingFilter}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onAgingFilterChange(e.target.value)
          }
          fullWidth
          sx={getReconFlagsFiltersDefaultSelectSx(theme) as any}
        >
          <MenuItem value='any'>{t('reconFlags.filters.any')}</MenuItem>
          {RECON_FLAGS_AGE_FILTER_DAYS.map((days) => (
            <MenuItem key={days} value={String(days)}>
              {t(`reconFlags.filters.${RECON_FLAGS_AGE_FILTER_I18N[days]}`)}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={getReconFlagsFiltersSearchWrapperSx() as any}>
          <Autocomplete
            testId='recon-flags-client-search'
            label=''
            placeholder={t('reconFlags.searchPlaceholder')}
            value={selectedClient}
            onChange={(
              _e: SyntheticEvent,
              next: ReconFlagsClientOption | null,
            ) => onSelectedClientChange(next)}
            onSearch={onSearchClients}
            onSearchError={onSearchClientsError}
            debounceMs={clientSearchDebounceMs}
            minChars={clientSearchMinChars}
            searchOnOpen={false}
            showSearchIcon
            fullWidth
            size='small'
            getOptionLabel={(option: ReconFlagsClientOption | string) =>
              typeof option === 'string'
                ? option
                : formatReconFlagsClientOptionLabel(option)
            }
            isOptionEqualToValue={(
              a: ReconFlagsClientOption,
              b: ReconFlagsClientOption,
            ) => a.id === b.id}
            sx={getReconFlagsFiltersSearchFieldSx(theme) as any}
          />
          <Tooltip
            title={t('reconFlags.searchHelp', {
              max: RECON_FLAGS_MAX_ACCOUNT_IDS,
            })}
          >
            <Box
              component='span'
              aria-label={String(t('reconFlags.searchHelpAria'))}
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: String(theme.color.onSurfaceVariant),
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <Icon icon={InfoOutlinedIcon} size='small' />
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </>
  );
};
